import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, X, Plus, Calendar, Building2, FileText, ThumbsUp, ThumbsDown, Minus, Filter } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";

interface FilterState {
  feedbackType: string;
  rating: number | undefined | null;
  userHash: string;
  startDate: string;
  endDate: string;
  page: number;
  limit: number;
}

interface ReportsFiltersProps {
  helpCenters: Array<{ id: number; title: string }>;
  selectedHelpCenterId: string;
  filters: FilterState;
  onHelpCenterChange: (helpCenterId: string) => void;
  onFiltersChange: (filters: Partial<FilterState>) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

export const ReportsFilters: React.FC<ReportsFiltersProps> = ({
  helpCenters,
  selectedHelpCenterId,
  filters,
  onHelpCenterChange,
  onFiltersChange,
  onClearFilters,
  hasActiveFilters,
}) => {
  const { t } = useTranslation();
  const selectedHelpCenter = helpCenters.find(
    (hc) => String(hc.id) === selectedHelpCenterId
  );

  const getActiveFilterCount = () => {
    let count = 0;
    if (selectedHelpCenterId) count++;
    if (filters.feedbackType) count++;
    if (filters.rating !== undefined) count++;
    if (filters.startDate || filters.endDate) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
      {/* Search Input */}
      <div className="relative flex-1 w-full sm:max-w-sm">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t("reports.filters.userHashPlaceholder")}
          value={filters.userHash}
          onChange={(e) =>
            onFiltersChange({
              userHash: e.target.value,
              page: 1,
            })
          }
          className="pl-9"
        />
      </div>

      {/* Filter Pills */}
      <div className="flex flex-wrap gap-2 items-center">
        {/* Help Center Filter */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="h-9 border-dashed"
            >
              <Building2 className="h-4 w-4 mr-1.5" />
              {selectedHelpCenter ? selectedHelpCenter.title : t("reports.filters.helpCenter")}
              {selectedHelpCenterId && (
                <Badge variant="secondary" className="ml-1.5 h-5 px-1.5">
                  {1}
                </Badge>
              )}
              <Plus className="h-4 w-4 ml-1.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="p-2">
              <Input
                placeholder={t("reports.filters.searchHelpCenters")}
                className="h-8"
              />
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onHelpCenterChange("")}>
              {t("reports.filters.allHelpCenters")}
            </DropdownMenuItem>
            {helpCenters.map((helpCenter) => (
              <DropdownMenuItem
                key={helpCenter.id}
                onClick={() => onHelpCenterChange(String(helpCenter.id))}
              >
                {helpCenter.title}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Type Filter */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="h-9 border-dashed"
            >
              <FileText className="h-4 w-4 mr-1.5" />
              {filters.feedbackType || t("reports.filters.type")}
              {filters.feedbackType && (
                <Badge variant="secondary" className="ml-1.5 h-5 px-1.5">
                  1
                </Badge>
              )}
              <Plus className="h-4 w-4 ml-1.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="p-2">
              <Input
                placeholder={t("reports.filters.searchTypes")}
                className="h-8"
              />
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() =>
                onFiltersChange({ feedbackType: "", page: 1 })
              }
            >
              {t("reports.filters.allTypes")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                onFiltersChange({ feedbackType: "article", page: 1 })
              }
            >
              {t("reports.filters.typeArticle")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                onFiltersChange({ feedbackType: "guide", page: 1 })
              }
            >
              {t("reports.filters.typeGuide")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                onFiltersChange({ feedbackType: "category", page: 1 })
              }
            >
              {t("reports.filters.typeCategory")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Rating Filter */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="h-9 border-dashed"
            >
              {filters.rating === 1 ? (
                <ThumbsUp className="h-4 w-4 mr-1.5" />
              ) : filters.rating === 0 ? (
                <ThumbsDown className="h-4 w-4 mr-1.5" />
              ) : filters.rating === null ? (
                <Minus className="h-4 w-4 mr-1.5" />
              ) : (
                <Filter className="h-4 w-4 mr-1.5" />
              )}
              {filters.rating === 1
                ? t("reports.positive")
                : filters.rating === 0
                ? t("reports.negative")
                : filters.rating === null
                ? t("reports.noRating")
                : t("reports.filters.rating")}
              {filters.rating !== undefined && (
                <Badge variant="secondary" className="ml-1.5 h-5 px-1.5">
                  1
                </Badge>
              )}
              <Plus className="h-4 w-4 ml-1.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="p-2">
              <Input
                placeholder={t("reports.filters.searchRatings")}
                className="h-8"
              />
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() =>
                onFiltersChange({ rating: undefined, page: 1 })
              }
            >
              {t("reports.filters.allRatings")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onFiltersChange({ rating: 1, page: 1 })}
            >
              <ThumbsUp className="h-4 w-4 mr-2 text-green-600" />
              {t("reports.positive")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onFiltersChange({ rating: 0, page: 1 })}
            >
              <ThumbsDown className="h-4 w-4 mr-2 text-red-600" />
              {t("reports.negative")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                onFiltersChange({ rating: null as any, page: 1 })
              }
            >
              <Minus className="h-4 w-4 mr-2" />
              {t("reports.noRating")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Date Range Filter */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="h-9 border-dashed"
            >
              <Calendar className="h-4 w-4 mr-1.5" />
              {t("reports.filters.dateRange")}
              {(filters.startDate || filters.endDate) && (
                <Badge variant="secondary" className="ml-1.5 h-5 px-1.5">
                  {(filters.startDate ? 1 : 0) + (filters.endDate ? 1 : 0)}
                </Badge>
              )}
              <Plus className="h-4 w-4 ml-1.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="start-date">{t("reports.filters.startDate")}</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    onFiltersChange({
                      startDate: e.target.value,
                      page: 1,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date">{t("reports.filters.endDate")}</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    onFiltersChange({ endDate: e.target.value, page: 1 })
                  }
                />
              </div>
              {(filters.startDate || filters.endDate) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    onFiltersChange({ startDate: "", endDate: "", page: 1 })
                  }
                  className="w-full"
                >
                  <X className="h-4 w-4 mr-1" />
                  {t("reports.filters.clearDates")}
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {/* Clear All Button */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="h-9"
          >
            <X className="h-4 w-4 mr-1" />
            {t("reports.filters.clearAll")}
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-1.5 h-5 px-1.5">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};
